import React from "react";
import { FiAlertTriangle, FiCheckCircle } from "react-icons/fi";
import { MiniBar } from "./irrigationHelpers";

const MAX_FLOW = 700;

const IrrWaterPressurePanel = ({ d }) => {
  const { pressure } = d;
  const faultZones = pressure.zones.filter(z => !z.normal);
  const maxFlow = Math.max(...pressure.flowTrend.map(x => x.flow), 1);

  return (
    <div className="irr-wp-panel">

      {/* ── Pump & Main Pipeline ── */}
      <div className="irr-wp-top">
        <div className="irr-pump-card">
          <div className="irr-section-title">Pump & Pipeline Status</div>
          <div className="irr-pump-status-row">
            <div className={`irr-pump-badge ${pressure.pumpStatus === "Running" ? "irr-pump-run" : "irr-pump-stop"}`}>
              ⚙ Pump: {pressure.pumpStatus}
            </div>
            <div className="irr-pump-badge irr-pump-info">🛡 Dry-run: {pressure.dryRunProtection}</div>
          </div>
          <div className="irr-pump-stats">
            {[
              { label: "Pump Pressure",  value: `${pressure.pumpPressure} bar` },
              { label: "Main Pipeline",  value: `${pressure.mainPipeline} bar` },
              { label: "Total Flow",     value: `${pressure.pumpFlowRate} L/min` },
              { label: "Run Hours",      value: `${pressure.pumpRunHours}h today` },
              { label: "Power Draw",     value: `${pressure.pumpPower} kW` },
            ].map(s => (
              <div key={s.label} className="irr-pump-stat">
                <div className="irr-pump-val">{s.value}</div>
                <div className="irr-pump-lbl">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Pressure gauge bar */}
          <div style={{ marginTop: 14 }}>
            <div className="irr-gauge-label">Main Pipeline Pressure: {pressure.mainPipeline} bar (Normal: 3.5–5.5 bar)</div>
            <div className="irr-gauge-track">
              <div className="irr-gauge-fill" style={{ width: `${(pressure.mainPipeline / 7) * 100}%` }} />
              <div className="irr-gauge-safe" />
            </div>
            <div className="irr-gauge-scale">
              <span>0</span><span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span><span>7 bar</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="irr-wp-alerts-card">
          <div className="irr-section-title">Pressure Alerts</div>
          {faultZones.length === 0 ? (
            <div className="irr-no-fault"><FiCheckCircle color="#10b981" size={18} /> All zones pressure normal</div>
          ) : faultZones.map(z => (
            <div key={z.zoneId} className="irr-fault-row">
              <FiAlertTriangle color="#ef4444" size={15} />
              <div>
                <div className="irr-fault-zone">{z.zoneId} — {z.name}</div>
                <div className="irr-fault-msg">{z.alert} ({z.pressure} bar)</div>
              </div>
            </div>
          ))}

          <div className="irr-section-title" style={{ marginTop: 16 }}>System Alerts</div>
          <div className="irr-system-alert-list">
            {faultZones.length > 0 && <div className="irr-sys-alert irr-sys-crit">🔴 Pipe fault suspected in {faultZones.map(z => z.zoneId).join(", ")}</div>}
            <div className="irr-sys-alert irr-sys-info">🟢 Pump running normally – no dry-run risk</div>
            <div className="irr-sys-alert irr-sys-warn">🟡 Abnormal flow spike detected at 08:00 – auto-investigated</div>
          </div>
        </div>
      </div>

      {/* ── Flow trend chart ── */}
      <div className="irr-flow-chart-card">
        <div className="irr-section-title">24h Flow Rate Trend (L/min)</div>
        <div className="irr-flow-chart">
          {pressure.flowTrend.map((pt, i) => (
            <div key={i} className="irr-flow-col">
              <div className="irr-flow-bar-wrap">
                <div className="irr-flow-bar" style={{ height: `${(pt.flow / maxFlow) * 100}%`, background: pt.flow > 500 ? "#ef4444" : "linear-gradient(180deg,#3b82f6,#6366f1)" }} />
              </div>
              <div className="irr-flow-val">{pt.flow > 0 ? pt.flow : ""}</div>
              <div className="irr-flow-lbl">{pt.t}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-zone pressure table ── */}
      <div className="irr-zp-table-card">
        <div className="irr-section-title">Per-Zone Pressure</div>
        <table className="irr-table">
          <thead>
            <tr>
              <th>Zone</th><th>Name</th><th>Pressure (bar)</th><th>Status</th><th>Pressure Bar</th>
            </tr>
          </thead>
          <tbody>
            {pressure.zones.map(z => (
              <tr key={z.zoneId} className={!z.normal ? "irr-tr-fault" : ""}>
                <td>{z.zoneId}</td>
                <td>{z.name}</td>
                <td style={{ fontWeight: 600, color: z.normal ? "#10b981" : "#ef4444" }}>{z.pressure} bar</td>
                <td>
                  {z.normal
                    ? <span className="irr-badge" style={{ background: "#d1fae5", color: "#10b981" }}>Normal</span>
                    : <span className="irr-badge" style={{ background: "#fee2e2", color: "#ef4444" }}>⚠ Fault</span>}
                </td>
                <td style={{ minWidth: 120 }}><MiniBar value={z.pressure} max={7} color={z.normal ? "#3b82f6" : "#ef4444"} height={8} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IrrWaterPressurePanel;
